import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useEventTracking } from '../hooks/useEventTracking';
import { useSession } from '../context/SessionContext';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/ui/ToastContainer';
import { Button } from './ui/Button';
import { Card, Input, Textarea, Select } from './ui/Button';
import { ContactInfo } from '@/components/ContactInfo';
import { formatPhoneNumber } from '../hooks/useFormatters';
import { useSpamPrevention } from '../hooks/useSpamPrevention';
import { Helmet } from "react-helmet-async";
import { CONTACT_DETAILS } from '@/config/contactDetails';

interface ContactFormData {
    name: string;
    email: string;
    company: string;
    message: string;
    source: string;
    phone: string;
    industry: string;
}

interface ContactResponse {
    success: boolean;
    message: string;
}

export const ContactPage: React.FC = () => {
    const navigate = useNavigate();
    const { sessionId } = useSession();
    const { trackPageView, trackContactFormSubmit, trackClick, isSessionReady } = useEventTracking();
    const { toasts, removeToast, success, error } = useToast();
    const { HoneypotField, validateSubmission } = useSpamPrevention();
    const [searchParams] = useSearchParams();

    const sourceMapping: { [key: string]: string } = {
        'pilot-program': 'pilot',
        'services': 'website-services',
        'about-pilot': 'pilot',
        'services-pilot': 'pilot',
        'services-predictive-intelligence': 'website-services',
        'services-system-integration': 'website-services',
        'services-implementation-support': 'website-services',
        'services-training-enablement': 'website-services',
        'methodology-monitor': 'website-services',
        'methodology-predict': 'website-services',
        'methodology-optimize': 'website-services',
        'methodology-preserve': 'website-services',
        'methodology-four-elements': 'pilot',
        'assessment': 'website-assessment',
        // External sources
        'search': 'search',
        'linkedin': 'social',
        'google-ads': 'advertisement',
        'referral': 'referral',
    };

    const [formData, setFormData] = useState<ContactFormData>({
        name: '',
        email: '',
        company: '',
        message: '',
        source: (() => {
            const urlSource = searchParams.get('source');
            if (urlSource) {
                return sourceMapping[urlSource] || 'other';
            }
            return '';
        })(),
        phone: '',
        industry: '',
    });

    const [errors, setErrors] = useState<Partial<ContactFormData>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const MIN_MESSAGE_LENGTH = 20;
    const MAX_MESSAGE_LENGTH = 2000;

    useEffect(() => {
        if (isSessionReady) {
            trackPageView('/contact');
        }
    }, [isSessionReady]);

    const validateForm = (): boolean => {
        const newErrors: Partial<ContactFormData> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
        } else if (formData.message.trim().length < MIN_MESSAGE_LENGTH) {
            newErrors.message = `Message must be at least ${MIN_MESSAGE_LENGTH} characters`;
        } else if (formData.message.length > MAX_MESSAGE_LENGTH) {
            newErrors.message = `Message must be less than ${MAX_MESSAGE_LENGTH} characters`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field: keyof ContactFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validation = validateSubmission();
        if (!validation.isValid) {
            return;
        }

        // Prevent double submission
        if (isSubmitting) return;

        if (!validateForm() || !sessionId) {
            error('Validation Error', 'Please fix the errors in the form before submitting.');
            return;
        }

        setIsSubmitting(true);

        try {
            const submission = {
                session_id: sessionId,
                name: formData.name.trim(),
                email: formData.email.trim(),
                company: formData.company.trim() || undefined,
                message: formData.message.trim(),
                source: formData.source || undefined,
                phone: formData.phone || undefined,
                industry: formData.industry || undefined,
            };

            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submission),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Server error: ${response.status}`);
            }

            const result: ContactResponse = await response.json();

            if (result.success) {
                setIsSubmitted(true);
                success('Message Sent!', 'We\'ll get back to you within 24 hours.');
                await trackContactFormSubmit(
                    formData.name,
                    formData.email,
                    formData.message,
                    formData.company || undefined,
                    formData.source || undefined,
                    formData.phone || undefined,
                );
            } else {
                throw new Error(result.message || 'Failed to submit contact form');
            }
        } catch (err) {
            console.error('Failed to submit contact form:', err);
            error(
                'Submission Failed',
                err instanceof Error
                    ? err.message
                    : 'Failed to submit your message. Please try again or contact us directly.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const messageLength = formData.message.length;
    const messageCharacterInfo = () => {
        if (messageLength === 0) return null;

        if (messageLength < MIN_MESSAGE_LENGTH) {
            return (
                <div className="text-sm text-orange-600">
                    {MIN_MESSAGE_LENGTH - messageLength} more characters needed
                </div>
            );
        }

        if (messageLength > MAX_MESSAGE_LENGTH * 0.9) {
            const remaining = MAX_MESSAGE_LENGTH - messageLength;
            return (
                <div className={`text-sm ${remaining < 50 ? 'text-red-600' : 'text-orange-600'}`}>
                    {remaining} characters remaining
                </div>
            );
        }

        return (
            <div className="text-sm text-green-600">
                ✓ Message length is good ({messageLength} characters)
            </div>
        );
    };

    if (isSubmitted) {
        return (
            <>
                <ToastContainer toasts={toasts} removeToast={removeToast} />
                <div className="min-h-screen bg-gray-50 py-12">
                    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                        <Card className="p-8 text-center">
                            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-6" />
                            <h1 className="text-3xl font-bold text-gray-900 mb-4">
                                Thank You!
                            </h1>
                            <p className="text-lg text-gray-600 mb-6">
                                We've received your message and one of our predictive analytics experts
                                will get back to you within 24 hours.
                            </p>
                            <div className="space-y-4">
                                <p className="text-gray-600">
                                    In the meantime, feel free to explore our resources or take our assessment
                                    to learn more about your predictive analytics readiness.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <Button
                                        onClick={() => {
                                            trackClick('assessment-from-contact', 'Assessment from Contact', 'button');
                                            navigate('/assessment');
                                        }}
                                    >
                                        Take Assessment
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            trackClick('home-from-contact', 'Home from Contact', 'button');
                                            navigate('/');
                                        }}
                                    >
                                        Back to Home
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>Contact {CONTACT_DETAILS.company.name} | Free Predictive Analytics Consultation | Cross-Industry AI</title>
                <meta name="description" content={`Get a free consultation on predictive analytics solutions across industries. Contact ${CONTACT_DETAILS.company.name} for AI-powered insights, data integration, and decision optimization in healthcare, manufacturing, agriculture, energy, and more.`} />
                <meta name="keywords" content="predictive analytics consultation, cross-industry AI consultation, data intelligence contact, machine learning consulting, business intelligence solutions" />
                <link rel="canonical" href="https://4castlabs.com/contact" />
                <meta property="og:title" content={`Contact ${CONTACT_DETAILS.company.name} | Free Consultation`} />
                <meta property="og:description" content={`Get a free consultation on predictive analytics solutions. Contact ${CONTACT_DETAILS.company.name} for AI-powered insights across industries.`} />
                <meta property="og:url" content="https://4castlabs.com/contact" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://4castlabs.com/images/logo.png" />
            </Helmet>
            <ToastContainer toasts={toasts} removeToast={removeToast}/>
            <div className="min-h-screen bg-gray-50 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Contact Information */}
                        <div className="lg:col-span-1">
                            <div className="mb-8">
                                <h1 className="text-3xl font-bold text-gray-900 mb-4">
                                    Get in Touch
                                </h1>
                                <p className="text-lg text-gray-600">
                                    Ready to transform your data into actionable intelligence? Our cross-industry experts are here to help.
                                </p>
                            </div>

                            <div className="space-y-6">
                                <ContactInfo
                                    variant="default"
                                    className="space-y-3"
                                    showPhone={false}
                                    emailType='email'
                                />
                            </div>

                            {/* Industry Focus */}
                            <div className="mt-8 p-6 bg-blue-50 rounded-lg">
                                <h3 className="font-semibold text-blue-900 mb-3">Industries We Serve</h3>
                                <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
                                    <div className="flex items-center">
                                        <span className="mr-2">🏥</span>Healthcare
                                    </div>
                                    <div className="flex items-center">
                                        <span className="mr-2">🏭</span>Manufacturing
                                    </div>
                                    <div className="flex items-center">
                                        <span className="mr-2">⚡</span>Energy
                                    </div>
                                    <div className="flex items-center">
                                        <span className="mr-2">🌲</span>Forestry
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 p-6 bg-primary-50 rounded-lg">
                                <h3 className="font-semibold text-primary-900 mb-2">
                                    What to Expect
                                </h3>
                                <ul className="space-y-2 text-sm text-primary-800">
                                    <li>• Response within 24 hours</li>
                                    <li>• Free initial consultation</li>
                                    <li>• Industry-specific solution recommendations</li>
                                    <li>• No obligation assessment</li>
                                </ul>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="lg:col-span-2">
                            <Card className="p-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                                    Send us a Message
                                </h2>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <HoneypotField />
                                        <Input
                                            label="Name *"
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            error={errors.name}
                                            placeholder="Your full name"
                                        />

                                        <Input
                                            label="Email *"
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            error={errors.email}
                                            placeholder="your.email@company.com"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <Input
                                            label="Company"
                                            type="text"
                                            value={formData.company}
                                            onChange={(e) => handleInputChange('company', e.target.value)}
                                            placeholder="Your company name"
                                        />

                                        <Select
                                            label="Industry"
                                            value={formData.industry}
                                            onChange={(e) => handleInputChange('industry', e.target.value)}
                                        >
                                            <option value="">Select your industry</option>
                                            <option value="healthcare">Healthcare</option>
                                            <option value="manufacturing">Manufacturing</option>
                                            <option value="energy">Energy</option>
                                            <option value="forestry">Forestry</option>
                                            <option value="other">Other</option>
                                        </Select>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <Input
                                            label="Phone"
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => {
                                                const formatted = formatPhoneNumber(e.target.value);
                                                handleInputChange('phone', formatted);
                                            }}
                                            placeholder="+1 (555) 123-4567"
                                        />

                                        <Select
                                            label="How did you hear about us?"
                                            value={formData.source}
                                            onChange={(e) => handleInputChange('source', e.target.value)}
                                        >
                                            <option value="">Select an option</option>
                                            <option value="search">Search Engine</option>
                                            <option value="referral">Referral</option>
                                            <option value="social">Social Media</option>
                                            <option value="event">Industry Event</option>
                                            <option value="advertisement">Advertisement</option>
                                            <option value="pilot">Pilot Program Interest</option>
                                            <option value="website-services">Your Services Page</option>
                                            <option value="website-assessment">Your Assessment Tool</option>
                                            <option value="other">Other</option>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Textarea
                                            label={`Message * (minimum ${MIN_MESSAGE_LENGTH} characters)`}
                                            value={formData.message}
                                            onChange={(e) => handleInputChange('message', e.target.value)}
                                            error={errors.message}
                                            placeholder="Tell us about your data challenges, current decision-making processes, and how predictive analytics could help optimize your operations..."
                                            rows={6}
                                        />
                                        {messageCharacterInfo()}
                                    </div>

                                    <div className="text-sm text-gray-500">
                                        <p>
                                            * Required fields. We respect your privacy and will never share your
                                            information. Read our <a href="/privacy" className="text-primary-600 hover:text-primary-700 underline">Privacy Policy</a>.
                                        </p>
                                    </div>

                                    <Button
                                        type="submit"
                                        size="lg"
                                        disabled={isSubmitting}
                                        className="w-full md:w-auto"
                                    >
                                        {isSubmitting ? 'Sending...' : 'Send Message'}
                                    </Button>
                                </form>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};